import { localStream } from '@stream'
import messenger from './Messenger'

export default class Connection {
  constructor(strategy) {
    this.strategy = strategy
    this.isReady = false
  }

  initialize() {
    // Named handlers: EventTarget ignores a listener that is already registered, so
    // every strategy switch no longer stacks another pair of replaceTrack listeners
    localStream.video.on('replaceTrack', this.handleLocalVideoReplaceTrack)
    localStream.audio.on('replaceTrack', this.handleLocalAudioReplaceTrack)
    localStream.video.on('addTrack', this.handleLocalAddTrack)
    localStream.audio.on('addTrack', this.handleLocalAddTrack)
    this.attachListeners()
    this.strategy.initialize()
    this.strategy.on('strategy:ready', this.handleReady)
    this.strategy.on('strategy:data', this.handleData)
    this.strategy.on('strategy:error', this.handleError)
    this.strategy.on('strategy:connections', this.handleConnections)
    this.strategy.on('strategy:recording:started', this.handleRecordingStarted)
    this.strategy.on('strategy:recording:stopped', this.handleRecordingStopped)
    this.strategy.on('strategy:failed', this.handleStrategyFailed)
    this.strategy.on('strategy:screensharingStarted:viewer', this.handleViewerScreensharingStarted)
    this.strategy.on('strategy:stream:audio', this.handleAudioStream)
    this.strategy.on('strategy:stream:video', this.handleVideoStream)
    return this
  }

  attachListeners = () => {
    messenger.subscribe(`connection:${this.strategy.name}:startRecording`, 'recording:start', this.startRecording)
    messenger.subscribe(`connection:${this.strategy.name}:stopRecording`, 'recording:stop', this.stopRecording)
  }

  handleLocalVideoReplaceTrack = event => {
    const { oldTrack, newTrack } = event.detail
    this.replaceLocalTrack('video', oldTrack, newTrack, localStream.video.stream.value)
  }

  handleLocalAudioReplaceTrack = event => {
    const { oldTrack, newTrack } = event.detail
    this.replaceLocalTrack('audio', oldTrack, newTrack, localStream.audio.stream.value)
  }

  // A track added (not replaced) mid-call, e.g. a kind unblocked after joining without it: P2P gives
  // it a sender, SFU a producer, if the call has none for that kind
  handleLocalAddTrack = event => {
    const track = event.detail
    this.strategy.addTrack?.(track.kind, track, localStream[track.kind].stream.value)
  }

  // strategy.replaceTrack is still invoked synchronously from the event dispatch; only a
  // failure (e.g. SFU producer closed or track already ended) is caught instead of
  // surfacing as an unhandled promise rejection
  replaceLocalTrack = async (type, oldTrack, newTrack, stream) => {
    try {
      await this.strategy.replaceTrack(type, oldTrack, newTrack, stream)
    } catch (error) {
      console.error(`[Connection] Replacing ${type} track on ${this.strategy.name} failed:`, error)
    }
  }

  replaceAudioTrack = ({ oldTrack, newTrack, stream }) => {
    this.strategy.replaceTrack('audio', oldTrack, newTrack, stream)
  }

  replaceVideoTrack = ({ oldTrack, newTrack, stream }) => {
    this.strategy.replaceTrack('video', oldTrack, newTrack, stream)
  }

  startScreensharing = async (stream) => {
    await this.strategy.shareScreen(stream)
  }

  stopScreensharing = () => {
    this.strategy.destroyScreensharing()
  }

  startRecording = data => {
    const { conference } = data
    this.strategy.startRecording(conference)
  }

  stopRecording = () => {
    this.strategy.stopRecording()
  }

  handleReady = event => {
    this.isReady = true
    messenger.emit('strategy:ready', event.detail)
  }

  handleData = event => {
    messenger.emit('strategy:data', event.detail)
  }

  handleVideoStream = event => {
    messenger.emit('strategy:stream:video', event.detail)
    messenger.emit('strategy:changed')
  }

  handleAudioStream = event => {
    messenger.emit('strategy:stream:audio', event.detail)
  }

  handleConnections = event => {
    messenger.emit('strategy:connections', event.detail)
  }


  handleViewerScreensharingStarted = event => {
    messenger.emit('strategy:screensharingStarted:viewer', event.detail)
  }

  handleRecordingStarted = event => {
    messenger.emit('strategy:recording:started', event.detail)
  }

  handleRecordingStopped = event => {
    messenger.emit('strategy:recording:stopped', event.detail)
  }

  handleError = event => {
    messenger.emit('strategy:error', event.detail)
  }

  broadcast = data => {
    if (this.isReady) {
      this.strategy.broadcast(data)
    }
  }

  async getTotalBits() {
    return await this.strategy.getTotalBits()
  }

  handleStrategyFailed = event => {
    messenger.emit('strategy:failed', event.detail)
  }

  destroy = () => {
    // The localStream replaceTrack/addTrack listeners are deliberately kept. After a websocket
    // reconnect (mediator.reset()) P2PStrategy can rebuild its peer connection from its
    // own signalling listeners before initialize() runs again, and initialize() re-adds
    // the same named handlers (no duplicates). On an inactive strategy the handler is a
    // no-op (no peer connection / no producers).
    messenger.unsubscribe(`connection:${this.strategy.name}:replaceTrack`)
    messenger.unsubscribe(`connection:${this.strategy.name}:replaceTrack`)
    messenger.unsubscribe(`connection:${this.strategy.name}:startScreensharing`)
    messenger.unsubscribe(`connection:${this.strategy.name}:stopScreensharing`)
    messenger.unsubscribe(`connection:${this.strategy.name}:startRecording`)
    messenger.unsubscribe(`connection:${this.strategy.name}:stopRecording`)
    this.strategy.off('strategy:ready', this.handleReady)
    this.strategy.off('strategy:data', this.handleData)
    this.strategy.off('strategy:error', this.handleError)
    this.strategy.off('strategy:connections', this.handleConnections)
    this.strategy.off('strategy:recording:started', this.handleRecordingStarted)
    this.strategy.off('strategy:recording:stopped', this.handleRecordingStopped)
    this.strategy.off('strategy:failed', this.handleStrategyFailed)
    this.strategy.off('strategy:screensharingStarted:viewer', this.handleViewerScreensharingStarted)
    this.strategy.off('strategy:stream:audio', this.handleAudioStream)
    this.strategy.off('strategy:stream:video', this.handleVideoStream)
    return this.strategy.destroy()
  }
}
